import AddEditFormCst from "@components/AddEditFormCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";

import { Box, Container } from "@mui/material";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { Association } from "src/interfaces/Common";
import { CreateCommunityCluster, GetClusterData } from "../types";
import { useAuth } from "@context/auth";

const AddCluster: React.FC<{ page: PagesID }> = ({ page }) => {
  const { t } = useTranslation(["community-cluster-page"]);
  const navigate = useNavigate();
  const { id, initiativeId } = useParams();
  const { user } = useAuth();
  const userQuery = new URLSearchParams();
  userQuery.append("corporate_id", id!);
  [
    "BUSINESS_ADMIN",
    "PRODUCT_OWNER",
    "TEACHER",
    "HR",
    "MANAGER",
    // "CONTENT_CREATOR",
    "MACHINE",
    "PROVIDER",
    "TUTOR",
    "ROLE_1",
    "ROLE_2",
    "ROLE_3",
    "ROLE_4",
    "ROLE_5",
    "ROLE_6",
    "ROLE_7",
    "ROLE_8",
    "ROLE_9",
    "ROLE_10",
    "ROLE_11",
    "ROLE_12",
    "ROLE_13",
    "ROLE_14",
    "ROLE_15",
    "ROLE_16",
    "ROLE_17",
    "ROLE_18",
    "ROLE_19",
    "ROLE_20",
  ].forEach((role) => {
    userQuery.append("roles", role);
  });

  const [openAssociations, setOpenAssociations] = useState<
    boolean | Association
  >(false);
  const [ownersAssociation, setOwnersAssociation] = useState<
    { id: number; label: string }[]
  >([
    {
      id: user!.id_user,
      label: `${user!.name} ${user!.surname}`,
    },
  ]);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const { columns, actionsForForms, dataDetail, mutate } = useFetchDataDetail(
    "",
    PagesID["corporate.initiative.community-cluster.add"],
  );

  const setOpenAssociation = (accessorKey: string, value: boolean) => {
    switch (accessorKey) {
      case "owners":
        setOpenAssociations(
          value
            ? {
                accessorKey: "owners",
                mediaType: "users",
                type: "table",
                titleKey: "owner-association",
                pageId: PagesID["users"],
                service: `${ServicesURL.digitedUsers}?${userQuery.toString()}`,
              }
            : value,
        );
        break;
    }
  };

  const getValues = (accessorKey: string) => {
    switch (accessorKey) {
      case "owners":
        return ownersAssociation;
    }
  };

  const handleDelete = (accessorKey: string, e: any) => {
    switch (accessorKey) {
      case "owners":
        setOwnersAssociation(
          ownersAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
    }
  };

  const handleAssociation = (accessorKey: string, value: any) => {
    switch (accessorKey) {
      case "owners":
        const objToTopicsAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return ownersAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setOwnersAssociation(objToTopicsAssociation);
        break;
    }
  };

  const { trigger: triggerCreateCommunityCluster } = useSWRMutation(
    ServicesURL.createCommunityCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!),
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.POST, { ...arg.arg }),
  );

  const { trigger: triggerCreateForum } = useSWRMutation(
    ServicesURL.postWrapper,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.POST, arg.arg),
  );

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

    const createBody: CreateCommunityCluster = {
      cluster: {
        corporate_id: +id!,
        initiative_id: +initiativeId!,
        is_post_enabled: inputForm.is_post_enabled,
        is_share_enabled: inputForm.is_share_enabled,
        is_talk_enabled: inputForm.is_talk_enabled,
        is_visible: inputForm.is_visible,
        name: inputForm.name,
      },
      owners: ownersAssociation.map(({ id }) => id),
    };

    try {
      const response: GetClusterData =
        await triggerCreateCommunityCluster(createBody);
      try {
        await triggerCreateForum({
          body: {
            name: `initiative_${initiativeId}_${response.cluster.id}`,
            langcode: "en",
          },
          method: `${MethodType.POST}`,
          endpoint: `${ServicesURL.getForum}`,
          initiativeid: initiativeId,
          clusterid: response.cluster.id,
          drupalid: response.cluster.drupal_id,
          corporateid: +id!,
        });
        mutate &&
          mutate(`${ServicesURL.postWrapper}?initiativeid=${initiativeId}`);
      } catch (error) {
        console.error("error", error);
      }
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
    }
    mutate && mutate();
  };

  return (
    <>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-community.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={
            openAssociations ? t("owners.hero-title") : t("add.hero-title")
          }
          description={""}
        />
        <Box
          sx={{
            mt: "25px",
            "&>.MuiGrid-root.MuiGrid-container>.MuiGrid-root.MuiGrid-item.MuiGrid-grid-xs-12>.MuiBox-root":
              {
                justifyContent: "flex-start",
                ".MuiGrid-root.MuiGrid-container:last-child": {
                  marginTop: "auto",
                },
              },
          }}
        >
          <AddEditFormCst
            actionsForForms={actionsForForms}
            columns={columns!}
            onSubmit={onSubmit}
            backButton={() => {
              navigate(-1);
            }}
            setOpenAssociation={(accessorKey, open) =>
              setOpenAssociation(accessorKey, open)
            }
            handleDelete={(accessorKey, el) => handleDelete(accessorKey, el)}
            handleAssociation={(accessorKey, value) =>
              handleAssociation(accessorKey, value)
            }
            getValues={(accessorKey) => getValues(accessorKey)}
            association={openAssociations}
          />
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </>
  );
};

export default AddCluster;
